import { useMemo } from 'react'
import { Star, LayoutGrid, FolderTree, Search, FileBarChart } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn } from '@/components/DataTable/types'
import { PERMISSIONS } from '@/lib/permissions'
import { SystemConfigForm } from '@/screens/settings/SystemConfigForm'
import {
  fetchFeatured,
  fetchMiniApps,
  fetchCategories,
  fetchSearchRanking,
  fetchCatalogReports,
  CAT_STR as S,
  type FeaturedEntryRow,
  type MiniAppRow,
  type CatalogCategoryRow,
  type SearchRankingRow,
  type CatalogReportRow,
} from './catalogApi'

const entityTypeOptions = [
  { value: 'sticker', label: 'sticker' },
  { value: 'emoji', label: 'emoji' },
  { value: 'miniapp', label: 'miniapp' },
]

/** A21.11 — Tanlangan (featured) katalog. Greenfield → fail-soft bo'sh. */
export function FeaturedCatalogScreen() {
  const columns = useMemo<AdminColumn<FeaturedEntryRow>[]>(
    () => [
      { key: 'title', header: S.title, type: 'text', minWidth: 220 },
      { key: 'entity_type', header: S.entityType, type: 'enum', width: 130, filterable: true, options: entityTypeOptions },
      { key: 'position', header: S.position, type: 'number', width: 100, align: 'right' },
      { key: 'active', header: S.activeFlag, type: 'bool', width: 130 },
      { key: 'curated_by', header: S.curatedBy, type: 'text', width: 160 },
    ],
    [],
  )
  return (
    <>
      <PageHeader title={S.featured} screenId="A21.11" />
      <DataTable<FeaturedEntryRow>
        screenId="A21.11"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetchFeatured}
        rowId={(r) => String(r.id)}
        exportable
        emptyState={{ icon: <Star size={22} /> }}
      />
    </>
  )
}

/** A21.12 — Mini App katalogi. Greenfield → fail-soft bo'sh. */
export function MiniAppCatalogScreen() {
  const columns = useMemo<AdminColumn<MiniAppRow>[]>(
    () => [
      {
        key: 'name',
        header: S.title,
        type: 'text',
        minWidth: 220,
        render: (r) => (
          <div className="min-w-0">
            <span className="block truncate font-medium text-primary">{r.name || '—'}</span>
            <span className="block truncate text-xs text-secondary">
              {r.short_name ? `@${r.short_name}` : ''}
              {r.owner ? ` · ${r.owner}` : ''}
            </span>
          </div>
        ),
      },
      { key: 'category', header: S.category, type: 'text', width: 150 },
      {
        key: 'status',
        header: S.status,
        type: 'enum',
        width: 140,
        filterable: true,
        options: [
          { value: 'active', label: S.active, status: 'active' },
          { value: 'pending', label: S.pending, status: 'pending' },
          { value: 'blocked', label: S.blocked, status: 'blocked' },
        ],
      },
      { key: 'opens', header: S.opens, type: 'number', width: 120, align: 'right' },
    ],
    [],
  )
  return (
    <>
      <PageHeader title={S.miniApps} screenId="A21.12" />
      <DataTable<MiniAppRow>
        screenId="A21.12"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetchMiniApps}
        rowId={(r) => String(r.id)}
        exportable
        emptyState={{ icon: <LayoutGrid size={22} /> }}
      />
    </>
  )
}

/** A21.13 — Katalog kategoriyalari. Greenfield → fail-soft bo'sh. */
export function CatalogCategoriesScreen() {
  const columns = useMemo<AdminColumn<CatalogCategoryRow>[]>(
    () => [
      { key: 'name', header: S.title, type: 'text', minWidth: 200 },
      { key: 'slug', header: S.slug, type: 'text', width: 160 },
      { key: 'kind', header: S.kind, type: 'enum', width: 130, filterable: true, options: entityTypeOptions },
      { key: 'items_count', header: S.items, type: 'number', width: 110, align: 'right' },
      { key: 'sort_order', header: S.sortOrder, type: 'number', width: 100, align: 'right' },
    ],
    [],
  )
  return (
    <>
      <PageHeader title={S.categories} screenId="A21.13" />
      <DataTable<CatalogCategoryRow>
        screenId="A21.13"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetchCategories}
        rowId={(r) => String(r.id)}
        exportable
        emptyState={{ icon: <FolderTree size={22} /> }}
      />
    </>
  )
}

/**
 * A21.14 — Tavsiya algoritmi sozlamalari. Endi JONLI: katalog ranking
 * vaznlari sof konfiguratsiya (app_settings `catalog_reco_*`), boshqa A24
 * config ekranlar kabi `/admin/settings/system` orqali round-trip qiladi
 * (SystemConfigForm). Soxta ma'lumot yo'q.
 */
export function RecommendationSettingsScreen() {
  return (
    <SystemConfigForm
      screenId="A21.14"
      title={S.recommendation}
      description={S.recoDesc}
      sections={[
        {
          title: S.recoSection,
          fields: [
            { key: 'catalog_reco_enabled', label: S.recoEnabled, type: 'bool', def: 'true' },
            { key: 'catalog_reco_installs_weight', label: S.recoInstalls, type: 'number', def: '50', min: 0, max: 100 },
            { key: 'catalog_reco_recency_weight', label: S.recoRecency, type: 'number', def: '20', min: 0, max: 100 },
            { key: 'catalog_reco_trending_weight', label: S.recoTrending, type: 'number', def: '20', min: 0, max: 100 },
            { key: 'catalog_reco_personalization_weight', label: S.recoPersonalization, type: 'number', def: '10', min: 0, max: 100 },
            { key: 'catalog_reco_max_results', label: S.recoMaxResults, type: 'number', def: '50', min: 1, max: 500 },
          ],
        },
      ]}
    />
  )
}

/** A21.15 — Qidiruv reytingi (katalog qidiruv boost qoidalari). Greenfield → fail-soft bo'sh. */
export function SearchRankingScreen() {
  const columns = useMemo<AdminColumn<SearchRankingRow>[]>(
    () => [
      { key: 'term', header: S.term, type: 'text', minWidth: 200 },
      { key: 'entity_type', header: S.entityType, type: 'enum', width: 130, filterable: true, options: entityTypeOptions },
      { key: 'boost', header: S.boost, type: 'number', width: 110, align: 'right' },
      { key: 'impressions', header: S.impressions, type: 'number', width: 120, align: 'right' },
      { key: 'clicks', header: S.clicks, type: 'number', width: 110, align: 'right' },
    ],
    [],
  )
  return (
    <>
      <PageHeader title={S.searchRanking} screenId="A21.15" />
      <DataTable<SearchRankingRow>
        screenId="A21.15"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetchSearchRanking}
        rowId={(r) => String(r.id)}
        exportable
        emptyState={{ icon: <Search size={22} /> }}
      />
    </>
  )
}

/** A21.18 — Katalog hisobotlari (davriy agregat). Greenfield → fail-soft bo'sh. */
export function CatalogReportsScreen() {
  const columns = useMemo<AdminColumn<CatalogReportRow>[]>(
    () => [
      { key: 'period', header: S.period, type: 'text', width: 140 },
      { key: 'entity_type', header: S.entityType, type: 'enum', width: 130, filterable: true, options: entityTypeOptions },
      { key: 'new_items', header: S.newItems, type: 'number', width: 110, align: 'right' },
      { key: 'installs', header: S.installs, type: 'number', width: 110, align: 'right' },
      { key: 'removals', header: S.removals, type: 'number', width: 110, align: 'right' },
      { key: 'generated_at', header: S.generatedAt, type: 'date', width: 150 },
    ],
    [],
  )
  return (
    <>
      <PageHeader title={S.reports} screenId="A21.18" />
      <DataTable<CatalogReportRow>
        screenId="A21.18"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetchCatalogReports}
        rowId={(r) => String(r.id)}
        exportable
        emptyState={{ icon: <FileBarChart size={22} /> }}
      />
    </>
  )
}
